"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect } from "react"

export interface SearchHistory {
  id: string
  name: string
  filters: {
    location?: string
    experience?: string
    skills?: string[]
    salary?: { min: number; max: number }
    jobType?: string
    remote?: boolean
  }
  resultsCount: number
  createdAt: Date
  lastUsed: Date
}

interface SearchHistoryContextType {
  searches: SearchHistory[]
  addSearch: (search: Omit<SearchHistory, "id" | "createdAt" | "lastUsed">) => void
  updateLastUsed: (searchId: string) => void
  getRecentSearches: (limit?: number) => SearchHistory[]
}

const SearchHistoryContext = createContext<SearchHistoryContextType | undefined>(undefined)

export function SearchHistoryProvider({ children }: { children: React.ReactNode }) {
  const [searches, setSearches] = useState<SearchHistory[]>([])

  // Load searches from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("searchHistory")
    if (saved) {
      const parsed = JSON.parse(saved).map((s: any) => ({
        ...s,
        createdAt: new Date(s.createdAt),
        lastUsed: new Date(s.lastUsed),
      }))
      setSearches(parsed)
    } else {
      // Add some mock data for demonstration
      const mockSearches: SearchHistory[] = [
        {
          id: "1",
          name: "Senior React Developers",
          filters: {
            skills: ["React", "TypeScript", "Node.js"],
            experience: "5+ years",
            location: "San Francisco, CA",
            remote: true,
          },
          resultsCount: 127,
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          lastUsed: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        },
        {
          id: "2",
          name: "DevOps Engineers",
          filters: {
            skills: ["AWS", "Docker", "Kubernetes"],
            experience: "3+ years",
            location: "Remote",
            salary: { min: 90000, max: 150000 },
          },
          resultsCount: 89,
          createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
          lastUsed: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        },
      ]
      setSearches(mockSearches)
    }
  }, [])

  // Save to localStorage whenever searches change
  useEffect(() => {
    localStorage.setItem("searchHistory", JSON.stringify(searches))
  }, [searches])

  const addSearch = (search: Omit<SearchHistory, "id" | "createdAt" | "lastUsed">) => {
    const newSearch: SearchHistory = {
      ...search,
      id: Date.now().toString(),
      createdAt: new Date(),
      lastUsed: new Date(),
    }
    setSearches((prev) => [newSearch, ...prev.slice(0, 19)]) // Keep only 20 most recent
  }

  const updateLastUsed = (searchId: string) => {
    setSearches((prev) => prev.map((search) => (search.id === searchId ? { ...search, lastUsed: new Date() } : search)))
  }

  const getRecentSearches = (limit = 2) => {
    return searches.sort((a, b) => b.lastUsed.getTime() - a.lastUsed.getTime()).slice(0, limit)
  }

  return (
    <SearchHistoryContext.Provider
      value={{
        searches,
        addSearch,
        updateLastUsed,
        getRecentSearches,
      }}
    >
      {children}
    </SearchHistoryContext.Provider>
  )
}

export function useSearchHistory() {
  const context = useContext(SearchHistoryContext)
  if (context === undefined) {
    throw new Error("useSearchHistory must be used within a SearchHistoryProvider")
  }
  return context
}
